import React from "react";
import { Check, X, ChevronUp, ChevronDown } from "lucide-react";

const DriverReportsSection = ({
  taskLoading,

  reports,
  filteredReports,

  searchTerm,
  setSearchTerm,

  statusFilter,
  setStatusFilter,

  selectedTask,
  setSelectedTask,

  showModal,
  setShowModal,

  showLogs,
  setShowLogs,

  assignmentLogs,
  setAssignmentLogs,

  statusChangeLogs,
  setStatusChangeLogs,

  fetchTaskLogs,

  fromDate,
  toDate,

  expandedRows,
  toggleRow,

  currentPage,
  setCurrentPage,
  totalCount,
  reportsPerPage,

  formatDistance,
  formatMinutes,
  formatTime,
  fmt2,

  renderWaitingTimeCell,
  renderTaskUpdateCell,
  renderDeliveryTimeCell,

  TablePagination,
  TaskDetailsModal,
}) => {
  return (
    <>
      {/* FILTER BAR */}
      <div className="bg-white p-4 rounded shadow mb-4 flex flex-wrap gap-4 items-center">
        <input
          type="text"
          placeholder="Search by Task ID, Order ID, Description"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border px-3 py-2 rounded w-64"
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border px-3 py-2 rounded"
        >
          <option value="all">All Status</option>
          <option value="ontime">On Time</option>
          <option value="late">Late</option>
        </select>
      </div>

      {/* REPORTS TABLE */}
      <div className="bg-white border rounded-lg shadow-sm mt-6">
        {taskLoading ? (
          <p className="p-4">Loading reports...</p>
        ) : reports.length === 0 ? (
          <p className="p-4 text-gray-400">
            No reports found for selected date range.
          </p>
        ) : (
          <>
            <div className="max-h-[460px] overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-20 bg-orange-400 text-white text-left">
                  <tr>
                    <th className="px-4 py-2">Task Id</th>
                    <th className="px-4 py-2">Order Id</th>
                    <th className="px-4 py-2">Description</th>
                    <th className="px-4 py-2">Actual Time</th>
                    <th className="px-4 py-2">Estimate</th>
                    <th className="px-4 py-2">Distance</th>
                    <th className="px-4 py-2">Delivery Date</th>
                    <th className="px-4 py-2">Waiting</th>
                    <th className="px-4 py-2">Task Update</th>
                    <th className="px-4 py-2">Charges</th>
                    <th className="px-4 py-2">Pickup Time</th>
                    <th className="px-4 py-2">Successful Time</th>
                    <th className="px-4 py-2">Time Details</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredReports.map((task, index) => (
                    <React.Fragment key={task.pk_task_id || index}>
                      <tr className="border-t">
                        <td
                          className="px-4 py-2 text-blue-600 cursor-pointer hover:underline"
                          onClick={() => {
                            setSelectedTask(task);
                            setShowModal(true);
                          }}
                        >
                          {task.pk_task_id}
                        </td>

                        <td className="px-4 py-2">{task.order_id}</td>

                        <td className="px-4 py-2">{task.task_description}</td>

                        <td className="px-4 py-2">
                          {task.actual_estimated_time
                            ? `${task.actual_estimated_time} min`
                            : "N/A"}
                        </td>

                        <td className="px-4 py-2">{task.estimated_time}</td>

                        <td className="px-4 py-2">
                          {formatDistance(task.distance)}
                        </td>

                        <td className="px-4 py-2">
                          {task.delivery_date ?? "N/A"}
                        </td>

                        <td className="px-4 py-2">
                          {renderWaitingTimeCell(task.waiting_time)}
                        </td>

                        <td className="px-4 py-2">
                          {renderTaskUpdateCell(task.task_update)}
                        </td>

                        <td className="px-4 py-2">₹ {fmt2(task.charges)}</td>

                        <td className="px-4 py-2">
                          {renderDeliveryTimeCell(task)}
                        </td>

                        <td className="px-4 py-2">
                          {task.pickeduptoSuccessful !== null
                            ? formatMinutes(Number(task.pickeduptoSuccessful))
                            : task.foodIsReadytoSuccessful !== null
                              ? formatMinutes(
                                  Number(task.foodIsReadytoSuccessful),
                                )
                              : "N/A"}
                        </td>

                        <td className="px-4 py-2">
                          <button
                            onClick={() => toggleRow(task.pk_task_id)}
                            className="px-2 py-1 text-xs bg-orange-100 text-orange-700 border border-orange-300 rounded hover:bg-orange-200 transition flex items-center gap-1"
                          >
                            {expandedRows[task.pk_task_id] ? (
                              <>
                                <ChevronUp size={14} />
                                Hide
                              </>
                            ) : (
                              <>
                                <ChevronDown size={14} />
                                Details
                              </>
                            )}
                          </button>
                        </td>
                      </tr>

                      {expandedRows[task.pk_task_id] && (
                        <tr className="border-t border-gray-200">
                          <td colSpan={13} className="px-6 py-6">
                            <div className="flex items-center justify-center">
                              {[
                                {
                                  label: "Acknowledged",
                                  time: task.acknowledged_time,
                                },
                                {
                                  label: "Arrived",
                                  time: task.arrival_time,
                                },
                                {
                                  label: "Food Ready",
                                  time: task.food_ready_time,
                                },
                                {
                                  label: "Driver Ready",
                                  time: task.driver_food_ready_time,
                                },
                                {
                                  label: "Picked Up",
                                  time: task.picked_up_time,
                                },
                                {
                                  label: "Delivered",
                                  time: task.delivery_time,
                                },
                              ].map((step, idx, arr) => (
                                <div key={idx} className="flex items-center">
                                  <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white shadow-md flex-shrink-0 ${
                                      step.time ? "bg-green-500" : "bg-red-400"
                                    }`}
                                  >
                                    {step.time ? (
                                      <Check size={18} strokeWidth={3} />
                                    ) : (
                                      <X size={18} strokeWidth={3} />
                                    )}
                                  </div>

                                  {idx < arr.length - 1 && (
                                    <div
                                      className={`h-0.5 w-24 flex-shrink-0 ${
                                        step.time && arr[idx + 1].time
                                          ? "bg-green-500"
                                          : "bg-red-300"
                                      }`}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>

                            <div className="flex items-start justify-center mt-2">
                              {[
                                {
                                  label: "Acknowledged",
                                  time: task.acknowledged_time,
                                },
                                {
                                  label: "Arrived",
                                  time: task.arrival_time,
                                },
                                {
                                  label: "Food Ready",
                                  time: task.food_ready_time,
                                },
                                {
                                  label: "Driver Ready",
                                  time: task.driver_food_ready_time,
                                },
                                {
                                  label: "Picked Up",
                                  time: task.picked_up_time,
                                },
                                {
                                  label: "Delivered",
                                  time: task.delivery_time,
                                },
                              ].map((step, idx, arr) => (
                                <div key={idx} className="flex items-start">
                                  <div className="flex flex-col items-center w-10 flex-shrink-0">
                                    <span className="text-xs text-gray-700 font-semibold text-center leading-tight whitespace-nowrap">
                                      {step.label}
                                    </span>

                                    <span
                                      className={`text-xs mt-0.5 text-center font-medium whitespace-nowrap ${
                                        step.time
                                          ? "text-gray-500"
                                          : "text-red-500"
                                      }`}
                                    >
                                      {formatTime(step.time)}
                                    </span>
                                  </div>

                                  {idx < arr.length - 1 && (
                                    <div className="w-24 flex-shrink-0" />
                                  )}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-center border-t bg-white py-4">
              <TablePagination
                currentPage={currentPage}
                totalCount={totalCount}
                pageSize={reportsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>

            <TaskDetailsModal
              show={showModal}
              task={selectedTask}
              onClose={() => {
                setShowModal(false);
                setShowLogs(false);
              }}
              showLogs={showLogs}
              setShowLogs={setShowLogs}
              assignmentLogs={assignmentLogs}
              statusChangeLogs={statusChangeLogs}
              onFetchLogs={async (taskId) => {
                const data = await fetchTaskLogs(taskId, fromDate, toDate);

                setAssignmentLogs(data?.assignmentLogs || []);
                setStatusChangeLogs(data?.statusChangeLogs || []);
              }}
            />
          </>
        )}
      </div>
    </>
  );
};

export default DriverReportsSection;